import React from 'react';
import { Users, Settings } from 'lucide-react';
import Select from 'react-select';
import { DealCommissionSection } from '../DealCommissionSection';
import type { Referrer, DealServiceFormData, DealCommissionOverrideFormData } from '../../../lib/types';
import { formatReferrerName } from '../../../lib/utils/referrerDisplayName';

interface DealStep1CommissionsTabProps {
  isDark: boolean;
  language: string;
  referrers: Referrer[];
  dealReferrerIds: string[];
  setDealReferrerIds: (ids: string[]) => void;
  selectStyles: any;
  // Commissions section
  dealId: string | undefined;
  clientId: string | undefined;
  assignedTo: string | undefined;
  serviceItems: DealServiceFormData[];
  commissionData: DealCommissionOverrideFormData;
  setCommissionData: (data: DealCommissionOverrideFormData) => void;
  singleInvoiceMode: boolean;
  singleInvoiceDate: Date | null;
  setSingleInvoiceDate: (d: Date | null) => void;
  overrideCommissionScaling: boolean | null;
  setOverrideCommissionScaling: (v: boolean | null) => void;
  hasUserEditedCommission: boolean;
  onUserEditCommission: () => void;
}

/**
 * Step 1, tab "Commissioni & Segnalatori": layout 2-colonne desktop
 * (selettore segnalatori a sinistra, calcolo commissioni a destra) +
 * stack singolo su mobile. Estratto da DealForm per separare il blocco
 * di ~160 righe dal main file.
 */
export function DealStep1CommissionsTab({
  isDark,
  language,
  referrers,
  dealReferrerIds,
  setDealReferrerIds,
  selectStyles,
  dealId,
  clientId,
  assignedTo,
  serviceItems,
  commissionData,
  setCommissionData,
  singleInvoiceMode,
  singleInvoiceDate,
  setSingleInvoiceDate,
  overrideCommissionScaling,
  setOverrideCommissionScaling,
  hasUserEditedCommission,
  onUserEditCommission,
}: DealStep1CommissionsTabProps) {
  const referrersSelect = (
    <Select
      isMulti
      options={referrers.map(referrer => ({
        value: referrer.id,
        label: `${formatReferrerName(referrer.full_name)} (${referrer.email})`,
      }))}
      value={dealReferrerIds
        .map(id => {
          const referrer = referrers.find(r => r.id === id);
          return referrer ? { value: referrer.id, label: `${formatReferrerName(referrer.full_name)} (${referrer.email})` } : null;
        })
        .filter(Boolean)}
      onChange={(selected: any) => {
        const selectedIds = selected ? selected.map((s: any) => s.value) : [];
        setDealReferrerIds(selectedIds);
      }}
      placeholder={language === 'it' ? 'Seleziona segnalatori...' : 'Select referrers...'}
      isClearable
      isSearchable
      classNamePrefix="react-select"
      styles={selectStyles}
    />
  );

  const commissionsSection = (
    <DealCommissionSection
      dealId={dealId}
      clientId={clientId}
      assignedTo={assignedTo}
      referrerIds={dealReferrerIds}
      serviceItems={serviceItems}
      onChange={setCommissionData}
      readonly={false}
      previewMode={false}
      singleInvoiceMode={singleInvoiceMode}
      singleInvoiceDate={singleInvoiceDate}
      onSingleInvoiceDateChange={setSingleInvoiceDate}
      language={language}
      overrideCommissionScaling={overrideCommissionScaling}
      onOverrideCommissionScalingChange={setOverrideCommissionScaling}
      hasUserEditedCommission={hasUserEditedCommission}
      onUserEdit={onUserEditCommission}
      initialCommissionData={commissionData}
    />
  );

  return (
    <div className="flex-1 overflow-hidden">
      {/* Desktop: 2-column grid */}
      <div className="hidden md:grid md:grid-cols-2 gap-0 h-full">
        <div className="modal-lead-left-column flex flex-col h-full overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-black dark:text-white" />
              <h3 className="text-lg font-medium text-black dark:text-white">
                {language === 'it' ? 'Segnalatori' : 'Referrers'}
              </h3>
            </div>
            <div className="space-y-4">
              <div className={isDark ? 'glass-select' : ''}>
                <label className="block text-sm font-medium text-black dark:text-white mb-2">
                  {language === 'it' ? 'Seleziona Segnalatori' : 'Select Referrers'}
                </label>
                {referrersSelect}
              </div>
              {dealReferrerIds.length > 0 && (
                <div className="text-sm text-black/60 dark:text-white/60">
                  {language === 'it'
                    ? `${dealReferrerIds.length} segnalatore${dealReferrerIds.length > 1 ? 'i' : ''} selezionato${dealReferrerIds.length > 1 ? 'i' : ''}`
                    : `${dealReferrerIds.length} referrer${dealReferrerIds.length > 1 ? 's' : ''} selected`}
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="bg-gray-100 dark:bg-gray-800/80 p-4 overflow-y-auto h-full"
          style={isDark ? {} : { backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(209 213 219 / 0.5) 1px, transparent 0)', backgroundSize: '24px 24px' }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Settings className="h-5 w-5 text-black dark:text-white" />
            <h3 className="text-lg font-medium text-black dark:text-white">
              {language === 'it' ? 'Commissioni' : 'Commissions'}
            </h3>
          </div>
          {commissionsSection}
        </div>
      </div>

      {/* Mobile: single column stack */}
      <div className="md:hidden space-y-4 p-4 overflow-y-auto h-full">
        <div className="glass-card rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-5 w-5 text-black dark:text-white" />
            <h3 className="text-lg font-medium text-black dark:text-white">
              {language === 'it' ? 'Segnalatori' : 'Referrers'}
            </h3>
          </div>
          <div className="space-y-4">
            <div className={isDark ? 'glass-select' : ''}>
              <label className="block text-sm font-medium text-black dark:text-white mb-2">
                {language === 'it' ? 'Seleziona Segnalatori' : 'Select Referrers'}
              </label>
              {referrersSelect}
            </div>
          </div>
        </div>

        <div className="glass-card rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="h-5 w-5 text-black dark:text-white" />
            <h3 className="text-lg font-medium text-black dark:text-white">
              {language === 'it' ? 'Commissioni' : 'Commissions'}
            </h3>
          </div>
          {commissionsSection}
        </div>
      </div>
    </div>
  );
}
